import { CascadiaMiniIcon } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { MouseEventHandler, useCallback } from "react";
import { TextFieldProps as AriaTextFieldProps } from "react-aria-components";

import { _TextField } from "./assets/_TextField";
import { Icon, IconProps } from "../../../foundations/icons/Icon/Icon";
import { _Text } from "../../_assets/_Text";
import { _Input } from "../_assets/_Input";
import { _InputWrapper } from "../_assets/_InputWrapper";
import {
  FieldErrors,
  FieldErrorsProps,
} from "../_assets/input-wrapper/FieldErrors";
import { FormFeedback } from "../_assets/input-wrapper/FormFeedback";
import { FormLabelRAC } from "../_assets/input-wrapper/FormLabelRAC";

export interface TextFieldProps extends AriaTextFieldProps {
  testID: string;
  info?: React.ReactNode;
  label?: string;
  errors?: FieldErrorsProps["children"];
  description?: string;
  placeholder?: string;
  successMessage?: string;
  clearable?: boolean;
  iconLeft?: CascadiaMiniIcon;
  iconLeftType?: IconProps["type"];
  iconRight?: CascadiaMiniIcon;
  iconRightType?: IconProps["type"];
  onRightIconClick?: MouseEventHandler<HTMLButtonElement>;
}

export function TextField(props: TextFieldProps) {
  const {
    testID,
    label,
    info,
    description,
    placeholder,
    clearable,
    iconLeftType,
    iconRightType,
    ...rest
  } = props;

  const handleClear = useCallback(() => {
    props.onChange?.("");
  }, [props]);

  let iconRight = props.iconRight;
  let onRightIconClick = props.onRightIconClick;
  if (clearable) {
    iconRight = "cancel";
    onRightIconClick = handleClear;
  }

  return (
    <_TextField {...rest}>
      <FormLabelRAC isRequired={props.isRequired} info={info}>
        {label}
      </FormLabelRAC>
      <_InputWrapper className={styles.relativeBlock}>
        {props.iconLeft && (
          <Icon
            name={props.iconLeft}
            color="icon-base"
            type={iconLeftType || "mini"}
            size="space-5"
          />
        )}
        <_Input
          className={css({
            paddingLeft: props.iconLeft ? "var(--space-9)" : "var(--space-3)",
            paddingRight: props.iconRight ? "var(--space-9)" : "var(--space-3)",
          })}
          data-testid={testID}
          placeholder={props.placeholder}
        />
        {iconRight && (
          <button
            className={css(styles.commonIcon, styles.rightIcon, {
              cursor: onRightIconClick ? "pointer" : "default",
            })}
            type="button"
            onClick={onRightIconClick}
          >
            <Icon
              name={iconRight}
              color="icon-base"
              type={iconRightType || "mini"}
              size="space-5"
            />
          </button>
        )}
      </_InputWrapper>
      {description && (
        <_Text style="input/helper" color="text-weak" slot="description">
          {description}
        </_Text>
      )}
      <FieldErrors>{props.errors}</FieldErrors>
      <FormFeedback type="valid">{props.successMessage}</FormFeedback>
    </_TextField>
  );
}

const styles = {
  relativeBlock: css({
    position: "relative",
    flexGrow: 1,
  }),
  commonIcon: css({
    padding: "0!important",
    position: "absolute",
    background: "none",
    border: "none",
  }),
  leftIcon: css({
    position: "absolute",
    left: "var(--space-3)",
    top: "var(--space-2-5)",
  }),
  rightIcon: css({
    position: "absolute",
    right: "var(--space-3)",
    top: "var(--space-2-5)",
  }),
};
